import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  MenuItem,
  IconButton,
  FormControlLabel,
  Checkbox,
  Typography,
  Grid,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

/** Returns { duplicateCombination: boolean, sameClassDifferentLevels: boolean } and messages for display. */
function validatePairs(pairs) {
  const complete = (pairs || []).filter(
    (p) => p.board != null && p.board !== '' && p.academic_year != null && p.academic_year !== '' && p.difficulty != null && p.difficulty !== ''
  );
  const keys = complete.map((p) => `${p.board}|${p.academic_year}|${p.difficulty}`);
  const keyCount = {};
  keys.forEach((k) => { keyCount[k] = (keyCount[k] || 0) + 1; });
  const duplicateCombination = Object.values(keyCount).some((c) => c > 1);

  const byClass = {};
  complete.forEach((p) => {
    const k = `${p.board}|${p.academic_year}`;
    if (!byClass[k]) byClass[k] = new Set();
    byClass[k].add(String(p.difficulty));
  });
  const sameClassDifferentLevels = Object.values(byClass).some((s) => s.size > 1);

  const messages = [];
  if (duplicateCombination) messages.push('The same Board, Year and Difficulty combination has been added more than once.');
  if (sameClassDifferentLevels) messages.push('For the same class (Board + Year), only one difficulty level is allowed.');
  return { duplicateCombination, sameClassDifferentLevels, messages };
}

const BoardYearPairs = ({
  pairs = [],
  boards = [],
  years = [],
  difficulties = [],
  onChange = () => {},
  getChapterForPair = null,
  requireChapter = false,
  chapterValidationReady = true,
  chapterDisabledReason = 'Select subject and topic to resolve chapter',
}) => {
  const [selectedYear, setSelectedYear] = useState('');

  const validation = useMemo(() => validatePairs(pairs), [pairs]);
  const showChapter = typeof getChapterForPair === 'function';
  const eligibleBoardsForSelectedYear = useMemo(() => {
    if (!showChapter || !requireChapter || !chapterValidationReady || !selectedYear) return boards;
    return boards.filter((board) => getChapterForPair({ board: board.id, academic_year: selectedYear }));
  }, [boards, chapterValidationReady, getChapterForPair, requireChapter, selectedYear, showChapter]);

  const handleSelectAllBoards = (checked) => {
    if (checked && selectedYear) {
      // Add all boards with the selected year
      const newPairs = eligibleBoardsForSelectedYear.map(board => ({
        board: board.id,
        academic_year: selectedYear,
        difficulty: null
      }));
      
      // Replace existing pairs with all boards
      onChange(null, 'replace', newPairs);
    } else if (!checked) {
      // Clear all pairs when unchecked
      onChange(null, 'replace', []);
    }
  };

  const handleYearChange = (yearId) => {
    setSelectedYear(yearId);
  };

  const isAllBoardsSelected = pairs.length === eligibleBoardsForSelectedYear.length && 
    pairs.every(pair => pair.academic_year === selectedYear) &&
    eligibleBoardsForSelectedYear.length > 0 &&
    eligibleBoardsForSelectedYear.every(board => pairs.some(pair => pair.board === board.id));

  return (
    <Stack spacing={2}>
      {/* Select All Boards Section */}
      <Box sx={{ 
        p: 2, 
        border: '1px solid #e0e0e0', 
        borderRadius: 1, 
        backgroundColor: '#fafafa' 
      }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Quick Selection
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isAllBoardsSelected}
                onChange={(e) => handleSelectAllBoards(e.target.checked)}
                disabled={!selectedYear}
              />
            }
            label="Select all boards"
            sx={{ m: 0, alignItems: 'flex-start' }}
          />
          <TextField
            select
            label="Year for all boards"
            value={selectedYear}
            onChange={(e) => handleYearChange(e.target.value)}
            sx={{ minWidth: { xs: 0, sm: 150 } }}
            fullWidth
            size="small"
          >
            {years.map(y => (
              <MenuItem key={y.id} value={y.id}>{y.year}</MenuItem>
            ))}
          </TextField>
        </Stack>
        {!selectedYear && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Please select a year to enable "Select all boards"
          </Typography>
        )}
        {selectedYear && showChapter && requireChapter && chapterValidationReady && eligibleBoardsForSelectedYear.length === 0 && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            No boards have a curriculum chapter for the selected topic and year.
          </Typography>
        )}
      </Box>

      {/* Validation errors */}
      {validation.messages.length > 0 && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {validation.messages.map((msg, i) => (
            <Typography key={i} component="span" variant="body2" sx={{ display: 'block' }}>
              {msg}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Individual Board-Year Pairs */}
      <Stack spacing={2}>
        {pairs.map((pair, idx) => {
          const chapter = showChapter ? getChapterForPair(pair) : null;
          const chapterMissing = showChapter && requireChapter && chapterValidationReady && pair.board && pair.academic_year && !chapter;
          return (
          <Grid key={idx} container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={showChapter ? 3 : 4}>
              <TextField 
                select 
                label="Board" 
                value={pair.board} 
                onChange={(e) => onChange(idx, 'board', e.target.value)} 
                fullWidth
                size="small"
              >
                {boards.map(b => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
                  
            <Grid item xs={12} sm={showChapter ? 2 : 3}>
              <TextField 
                select 
                label="Year" 
                value={pair.academic_year} 
                onChange={(e) => onChange(idx, 'academic_year', e.target.value)} 
                fullWidth
                size="small"
              >
                {years.map(y => (
                  <MenuItem key={y.id} value={y.id}>{y.year}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {showChapter && (
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Chapter"
                  value={chapter?.name || ''}
                  fullWidth
                  size="small"
                  disabled
                  error={chapterMissing}
                  helperText={chapterMissing ? 'Selected topic is not part of the curriculum for this board/year' : (!pair.board || !pair.academic_year ? chapterDisabledReason : '')}
                />
              </Grid>
            )}
            
            <Grid item xs={12} sm={showChapter ? 3 : 4}>
              <TextField 
                select 
                label="Difficulty *" 
                value={pair.difficulty || ''} 
                onChange={(e) => onChange(idx, 'difficulty', e.target.value)} 
                fullWidth
                size="small"
                required
                disabled={chapterMissing}
                helperText={chapterMissing ? 'Choose a board/year where this topic is in the curriculum' : ''}
              >
                {difficulties.map(d => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
                  
            <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconButton 
                disabled={pairs.length === 1} 
                onClick={() => onChange(idx, 'remove')}
                aria-label="Remove board-year pair"
                size="small"
              >
                <DeleteOutlineIcon fontSize="small"/>
              </IconButton>
            </Grid>
          </Grid>
          );
        })}
        <Button 
          startIcon={<AddIcon/>} 
          onClick={() => onChange(null, 'add')}
          variant="outlined"
          sx={{ mt: 1 }}
        >
          Add Board-Year
        </Button>
      </Stack>
    </Stack>
  );
};

export default BoardYearPairs;
